/**
 * 파일명 : usr0302m.jsx
 * 설명 : 경력관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-12-11    김채은     최초작성
 * 1.1       2024-03-11    기라성     회사 및 근무경력 저장 시 rowStsCd 값으로 입력, 수정, 삭제하도록 수정
 * 1.2       2026-03-20    황은택     jsx -> tsx 전환 작업
 * 
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0302mAction } from '../../../redux/slice/usr/usr0302m_slice';
import { IInquiryCndsState, IUserItemState, ITecnlLvlCdState, IExTecnlLvlCdState, ICertTpCdState, ITEduTpCdState, IworkExpreListState } from '../../../redux/models/usr/usr0302m_model';

/*  AntD  */
import { Button, Descriptions, Space, Select, Row, Col, Layout, Typography, Collapse, Input, DatePicker, ConfigProvider, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined, ExclamationCircleOutlined, PlusOutlined, MinusOutlined, PlusSquareOutlined, CheckSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import dayjs from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/

const Usr0302m = (props: any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch                   = useAppDispatch();
    const inquiryCnds                = useAppSelector((state) => state.Usr0302m.inquiryCnds);
    const userList                   = useAppSelector((state) => state.Usr0302m.userList);
    const tecnlLvlList               = useAppSelector((state) => state.Usr0302m.tecnlLvlList);
    const exTecnlLvlList             = useAppSelector((state) => state.Usr0302m.exTecnlLvlList);
    const userPersonalInformation    = useAppSelector((state) => state.Usr0302m.userPersonalInformation);
    const userSchStatusList          = useAppSelector((state) => state.Usr0302m.userSchStatusList);
    const userCertStatusList         = useAppSelector((state) => state.Usr0302m.userCertStatusList);
    const userWorkExpreStatusList    = useAppSelector((state) => state.Usr0302m.userWorkExpreStatusList);
    const userDevelopCareerStateList = useAppSelector((state) => state.Usr0302m.userDevelopCareerStateList);
    const current                    = useAppSelector((state) => state.Usr0302m.current);
    const pageSize                   = useAppSelector((state) => state.Usr0302m.pageSize);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography;
    const { Panel }   = Collapse;
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        let newCnds;

        if (!common.isEmptyString(props.empNo)) {
            newCnds = {
                instCd: props.instCd,
                empNo:  props.empNo,
                userId: '',
                userNm: '',
                baseDt: '',
            };
        }
        else {
            newCnds = {
                instCd: '002',
                empNo:  common.getEmpNo(),
                userId: common.getUserId(),
                userNm: common.getUserNm(),
                baseDt: '',
            };
        }

        // 조회조건 set
        dispatch(Usr0302mAction.setInquiryCnds(newCnds));
        // 생년월일 조회
        fn_Birth(newCnds);

        }, []);

    // 콤보박스
    React.useEffect(() => {

        // 직원명 콤보박스
        fn_User();
        // 기술등급 콤보박스
        fn_TecnlLvlCd();
        // 예상등급 콤보박스
        fn_exTecnlLvlCd();

    }, []);

    // 조회
    React.useEffect(() => {
        if (!inquiryCnds.instCd && !inquiryCnds.empNo) {
            return;
        }
        
        // 신상정보 조회
        fn_PersonalInformation();
        // 학력 현황 조회
        fn_SchStatusList();
        // 자격증 현황 조회
        fn_CertStatusList();
        // 회사 근무 경력 조회
        fn_WorkExpreStatusList();
        // 개발 경력 조회
        fn_DevelopCareerStateList();

    }, [inquiryCnds]);

    // 사원번호(조회조건) 변경 시 생년월일 조회
    React.useEffect(() => {
    if (inquiryCnds.empNo) {
        fn_Birth(inquiryCnds);
    }
    }, [inquiryCnds.empNo]);

    /* Table Columns */
    /* 학력 현황 */
    const schStatusColumns = React.useCallback(() => {
        const arr: any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>학교명</div>,
                dataIndex: 'schNm',
                key:       'schNm',
                align:     'center',
                width:     '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>입학년월</div>,
                dataIndex: 'entrYm',
                key:       'entrYm',
                align:     'center',
                width:     '10%',
                render: (text: string) => text ? commonDate.maskDate(text, 'YYYY-MM') : '',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>졸업년월</div>,
                dataIndex: 'gradYm',
                key:       'gradYm',
                align:     'center',
                width:     '10%',
                render: (text: string) => text ? commonDate.maskDate(text, 'YYYY-MM') : '',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>학과(전공)</div>,
                dataIndex: 'edptNm',
                key:       'edptNm',
                align:     'center',
                width:     '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>학위</div>,
                dataIndex: 'degreeNm',
                key:       'degreeNm',
                align:     'center',
                width:     '20%',
            }
        , ];

        return arr;
    }, []);

    /* 자격증 현황 */
    const certStatusColumns = React.useCallback(() => {
        const arr: any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>취득일자</div>,
                dataIndex: 'certDt',
                key:       'certDt',
                align:     'center',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>자격증</div>,
                dataIndex: 'certNm',
                key:       'certNm',
                align:     'center',
                width:     '10%',
            }
        , ];

        return arr;
    }, []);

    /* 회사 근무 경력 */
    const workExpreColumns = React.useCallback(() => {
        const arr: any = [
            {
                title:     '',
                dataIndex: 'rowStsCd',
                key:       'rowStsCd',
                align:     'center',
                width:     '2%',
                render: (_: any, record: any) => {
                    switch (record.rowStsCd) {
                        case 'N': return <PlusSquareOutlined  style={{ fontSize: 25, color: '#08c' }} />;
                        case 'U': return <CheckSquareOutlined style={{ fontSize: 25, color: '#08c' }} />;
                        case 'D': return <MinusSquareOutlined style={{ fontSize: 25, color: '#08c' }} />;
                        default: return null;
                    }
                }
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>* 회사명</div>,
                dataIndex: 'corpNm',
                key:       'corpNm',
                align:     'left',
                width:     '30%',
                render: (_: any, record: any) => (
                    <Space>
                        <Input
                            style        = {{ width: 330 }}
                            maxLength    = {100}
                            autoComplete = 'off'
                            id           = 'inputCorpNm'
                            name         = 'corpNm'
                            value        = {record.corpNm}
                            onChange     = { (e) => {
                                const newList = userWorkExpreStatusList.map(item =>
                                    item.id === record.id 
                                        ? { ...item, corpNm: e.target.value, rowStsCd: item.rowStsCd === 'N' ? 'N' : 'U' } 
                                        : item
                                );
                                dispatch(Usr0302mAction.setUserWorkExpreStatusList(newList));
                            }}
                        />
                    </Space>
                )
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>* 근무시작년월</div>,
                dataIndex: 'workStYm',
                key:       'workStYm',
                align:     'left',
                width:     '10%',
                render: (_: any, record: any) => (
                    <ConfigProvider locale={locale}>
                        <DatePicker
                            style      = {{ width: 150 }}
                            allowClear = {false}
                            picker     = 'month'
                            format     = {'YYYY-MM'}
                            id         = {'calendarWorkStYm'}
                            value={record.workStYm ? dayjs(record.workStYm, 'YYYYMM') : null}
                            onChange={(date) => {
                                const formatted = date ? dayjs(date).format('YYYYMM') : '';

                                const newList = userWorkExpreStatusList.map(item =>
                                    item.id === record.id
                                        ? {
                                            ...item,
                                            workStYm: formatted,
                                            rowStsCd: item.rowStsCd === 'N' ? 'N' : 'U'
                                        }
                                        : item
                                );
                                dispatch(Usr0302mAction.setUserWorkExpreStatusList(newList));
                            }}
                        />
                    </ConfigProvider>
                )
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>* 근무종료년월</div>,
                dataIndex: 'workEdYm',
                key:       'workEdYm',
                align:     'left',
                width:     '10%',
                render: (_: any, record: any) => (
                    <ConfigProvider locale={locale}>
                        <DatePicker
                            style      = {{ width: 150 }}
                            allowClear = {false}
                            picker     = 'month'
                            format     = {'YYYY-MM'}
                            id         = {'calendarWorkEdYm'}
                            value={record.workEdYm ? dayjs(record.workEdYm, 'YYYYMM') : null}
                            onChange={(date) => {
                                const formatted = date ? dayjs(date).format('YYYYMM') : '';

                                const newList = userWorkExpreStatusList.map(item =>
                                    item.id === record.id
                                        ? {
                                            ...item,
                                            workEdYm: formatted,
                                            rowStsCd: item.rowStsCd === 'N' ? 'N' : 'U'
                                        }
                                        : item
                                );
                                dispatch(Usr0302mAction.setUserWorkExpreStatusList(newList));
                            }}
                        />
                    </ConfigProvider>
                )
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>* 담당업무내용</div>,
                dataIndex: 'bizCntnt',
                key:       'bizCntnt',
                align:     'left',
                width:     '48%',
                render: (_: any, record: any) => (
                    <Space>
                        <Input
                            style        = {{ width: 600 }}
                            maxLength    = {500}
                            autoComplete = 'off'
                            id           = {'inputBizCntnt'}
                            name         = 'bizCntnt'
                            value        = {record.bizCntnt}
                            onChange={ (e) => {
                                const newList = userWorkExpreStatusList.map(item =>
                                    item.id === record.id 
                                        ? { ...item, bizCntnt: e.target.value, rowStsCd: item.rowStsCd === 'N' ? 'N' : 'U' } 
                                        : item
                                );
                                dispatch(Usr0302mAction.setUserWorkExpreStatusList(newList));
                            }}
                        />
                    </Space>
                )
            }
        , ];

        return arr;
    }, [userWorkExpreStatusList]);

    /* 회사 근무 경력 */
    const workColumns = React.useMemo(() => workExpreColumns(), [userWorkExpreStatusList]);
    
    /* 개발 경력 */
    const developCareerColumns = React.useCallback(() => {
        const arr: any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>시작일자</div>,
                dataIndex: 'pjStartDt',
                key:       'pjStartDt',
                align:     'center',
                width:     '10%',
                render: (text: string) => text ? commonDate.maskDate(text, 'YYYY-MM-DD') : '',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>종료일자</div>,
                dataIndex: 'pjEndDt',
                key:       'pjEndDt',
                align:     'center',
                width:     '10%',
                render: (text: string) => text ? commonDate.maskDate(text, 'YYYY-MM-DD') : '',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트명</div>,
                dataIndex: 'pjNm',
                key:       'pjNm',
                align:     'left',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>고객사</div>,
                dataIndex: 'cusNm',
                key:       'cusNm',
                align:     'left',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>개발언어</div>,
                dataIndex: 'pjDevtool',
                key:       'pjDevtool',
                align:     'left',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>개발형태</div>,
                dataIndex: 'pjDevformType',
                key:       'pjDevformType',
                align:     'center',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>개발유형</div>,
                dataIndex: 'pjDevTypeCd',
                key:       'pjDevTypeCd',
                align:     'center',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>업무</div>,
                dataIndex: 'pjDevBizCntnt',
                key:       'pjDevBizCntnt',
                align:     'center',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>역할</div>,
                dataIndex: 'pjDevRoleNm',
                key:       'pjDevRoleNm',
                align:     'center',
                width:     '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (_: any, record: any) => (
                    common.isAdmin() || common.getEmpNo() === record.empNo
                        ? <Button
                            size      = 'small'
                            id        = 'btn_OpenPopup_Dept_Detail'
                            name      = 'btn_OpenPopup_Dept_Detail'
                            className = 'background-color-default-button'
                            data-id   = {record.id}
                            onClick   = {(e) => onClick(e, record)}
                        >
                            상세보기
                        </Button>
                        : null
                ),
                align: 'center',
                width: '9%',
            }
        , ];

        return arr;
    }, []);

    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: "A"
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0003",
            success: (responseData: any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a: IUserItemState, b: IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0302mAction.setUserList(responseData.userList));
            },
        });
    };

    /**
     * 기능 : fn_Birth
     * 설명 : 생년월일 데이터 조회
     * param : 
     * return : None
     **/
    const fn_Birth = (cnds: any) => {

        const requestData = {
            userStCd: "A",
            empNo: cnds.empNo
        };

        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0003",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setInquiryCnds({
                    birdDt: responseData.userList[0].birdDt
                }));
            },
        });
    };

    /**
     * 기능 : 직원명 콤보박스 설정
     * param : 
     */
    const userOptions = React.useCallback(() => {
        /* 변수선언 및 셋팅 영역 */
        let arrUserOptions: IUserItemState[] = [];

        userList.forEach((item: IUserItemState) => {
            arrUserOptions.push({
                label:  item.userNm,
                value:  item.empNo,
                birdDt: item.birdDt
            });
        });
        
        return arrUserOptions;
    }, [userList]);

    /**
     * 기능 : fn_TecnlLvlCd
     * 설명 : 기술등급 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_TecnlLvlCd = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            instCd:  inquiryCnds.instCd,
            cdGrpId: "025"
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRCOMR0001",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setTecnlLvlList(responseData.codeItems));
            },
        });
    };

    /**
     * 기능 : 기술등급 콤보박스 설정
     * param : 
     */
    const tecnlLvlCdOptions = React.useCallback(() => {
        /* 변수선언 및 셋팅 영역 */
        let arrTecnlLvlCdOptions: ITecnlLvlCdState[] = [];
        
        tecnlLvlList.forEach((item: ITecnlLvlCdState) => {
            arrTecnlLvlCdOptions.push({
                label: item.cdNm,
                value: item.cd
            });
        });
        
        return arrTecnlLvlCdOptions;
    }, [tecnlLvlList]);

    /**
     * 기능 : fn_exTecnlLvlCd
     * 설명 : 예상등급 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_exTecnlLvlCd = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            instCd:  inquiryCnds.instCd,
            cdGrpId: "025"
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRCOMR0001",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setExTecnlLvlList(responseData.codeItems));
            },
        });
    };

    /**
     * 기능 : 예상등급 콤보박스 설정
     * param : 
     */
    const exTecnlLvlCdOptions = React.useCallback(() => {
        /* 변수선언 및 셋팅 영역 */
        let arrExTecnlLvlCdOptions: IExTecnlLvlCdState[] = [];
        
        exTecnlLvlList.forEach((item: IExTecnlLvlCdState) => {
            arrExTecnlLvlCdOptions.push({
                label: item.cdNm,
                value: item.cd
            });
        });
        
        return arrExTecnlLvlCdOptions;
    }, [exTecnlLvlList]);

    /**
     * 기능 : 조회 (신상정보)
     * param : 
     */
    const fn_PersonalInformation = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0012",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setUserPersonalInformation(responseData.personalInformation));
            },
        });
    };

    /**
     * 기능 : 조회 (학력 현황)
     * param : 
     */
     const fn_SchStatusList = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0014",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setUserSchStatusList(responseData.schStatusList));
            },
        });
    };

    /**
     * 기능 : 조회 (자격증 현황)
     * param : 
     */
     const fn_CertStatusList = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0015",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setUserCertStatusList(responseData.certStatusList));
            },
        });
    };

    /**
     * 기능 : 조회 (회사 근무 경력)
     * param : 
     */
     const fn_WorkExpreStatusList = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0016",
            success: (responseData: any) => {
                // 회사근무경력 근무년월 기준 정렬
                responseData.workExpreList.sort((a: IworkExpreListState, b: IworkExpreListState) => (a.workStYm < b.workStYm) ? 1 : -1)
                dispatch(Usr0302mAction.setUserWorkExpreStatusList(responseData.workExpreList));
            },
        });
    };
    
    /**
     * 기능 : 조회 (개발 경력)
     * param : 
     */
     const fn_DevelopCareerStateList = (empNo?: string) => {
        const requestData: IInquiryCndsState = {
            ...inquiryCnds,
            empNo: empNo ?? inquiryCnds.empNo
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0018",
            success: (responseData: any) => {
                dispatch(Usr0302mAction.setUserDevelopCareerStateList(responseData.developCareerList));
            },
        });
    };

    /**
      * 기능 : 입력 후 Enter key 입력 시 조회
      * 설명 : 입력컴포넌트
      * param : 
      * return : None
      */
     const handleOnKeyUp = (e: any) => {
        if (e.key === "Enter") {
            // 신상정보 조회
            fn_PersonalInformation();
            // 학력 현황 조회
            fn_SchStatusList();
            // 자격증 현황 조회
            fn_CertStatusList();
            // 회사 근무 경력 조회
            fn_WorkExpreStatusList();
            // 개발 경력 조회
            fn_DevelopCareerStateList();
        }
    };

    /**
      * 기능 : 초기화 함수
      * param : 
      */
    const fn_Reset = () => {
        dispatch(Usr0302mAction.setInquiryCnds({ ...inquiryCnds, empNo: common.getEmpNo(), birdDt: null }));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e: any, record?: any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            // 초기화 버튼
            case "btn_Reset" : {
                fn_Reset();
                break;
            }
            
            // 조회 버튼
            case "btn_Search" : {
                // 신상정보 조회
                fn_PersonalInformation();
                // 학력 현황 조회
                fn_SchStatusList();
                // 자격증 현황 조회
                fn_CertStatusList();
                // 회사 근무 경력 조회
                fn_WorkExpreStatusList();
                // 개발 경력 조회
                fn_DevelopCareerStateList();
                break;
            }
            
            // 워드파일 내려받기 버튼
            case "btn_Docx" : {
                docxDownload();
                break;
            }

            // 엑셀파일 내려받기 버튼
            case "btn_Excel" : {
                excelDownload();
                break;
            }

            // 기술등급 툴팁 버튼 
            case "btn_OpenPopup_Tooltip" : {
                const params: any = {};
                params.title      = "기술등급 설명";
                params.popupId    = "usr0302p1";
                params.width      = 1020;
                common.openPopup(params);
                break;
            }

            // 신상정보 저장 버튼
            case "btn_Save_Personal" : {
                // 기관코드 확인
                if (!inquiryCnds.instCd) {
                    const param = {
                        title:    "위드정보 확인",
                        contents: "기관코드 확인이 필요합니다.",
                    };

                    common.openInfo(param);
                    return;
                }

                // 직원번호 확인
                if (!common.isAdmin() && (inquiryCnds.empNo !== common.getEmpNo())) {
                    const param = {
                        title:    "위드정보 확인",
                        contents: "직원번호의 확인이 필요합니다.",
                    };

                    common.openInfo(param);
                    return;
                }

                const param = {
                    title:    "위드정보 확인",
                    contents: "신상정보를 저장하시겠습니까?",
                    accept: () => {
                        /* 변수선언 및 셋팅 영역 */
                        // 입력파라미터
                        const requestData = {
                            instCd:       inquiryCnds.instCd,
                            empNo:        inquiryCnds.empNo,
                            personalData: userPersonalInformation,
                        };
                        
                        /* 트렌젝션 호출 및 결과 셋팅 */
                        commonProxy.callAsyncSvc(requestData, {
                            txCd: "TRUSRS0013",
                            success: (_responseData: any) => {
                                
                                const param = {
                                    title:    "위드정보 확인",
                                    contents: "정상적으로 처리되었습니다.",
                                };

                                common.openAlert(param);

                                // 신상정보 재조회
                                fn_PersonalInformation();
                            },
                        });
                    }
                }
                common.openConfirm(param);

                break;
            }

            // 회사 근무 경력 row 추가(+) 버튼
            case "btn_Plus_Cert" : {
                const addRowId = userWorkExpreStatusList.length ? Math.max(...userWorkExpreStatusList.map(item => item.id)) + 1 : 1;

                const addRowNew = {
                    id:       addRowId,
                    rowStsCd: "N",        // 신규 row
                    corpNm:   "",
                    workStYm: "",
                    workEdYm: "",
                    bizCntnt: "",
                    careerSeq: 0,
                };
            
                dispatch(Usr0302mAction.setUserWorkExpreStatusList([...userWorkExpreStatusList, addRowNew]));

                break;
            }

            // 회사 근무 경력 row 삭제(-) 버튼
            case "btn_Minus_Cert" : {
                if (!selectedWorkRowKeys.length) {
                    common.openAlert({
                        title:    "위드정보 확인",
                        contents: "삭제할 행을 선택해주세요.",
                    });
                    break;
                }
                
                const newList = userWorkExpreStatusList.map(item => {
                        if (!selectedWorkRowKeys.includes(item.id)) return item;   // 체크 안된 행은 그대로
                        if (item.rowStsCd === "N") return null;                    // 신규 row 삭제
                        return {...item, rowStsCd: "D"};
                    })
                    .filter(Boolean);   // null 제거

                dispatch(Usr0302mAction.setUserWorkExpreStatusList(newList));

                break;
            }
            
            // 회사 근무 경력 저장 버튼
            case "btn_Add_Work" : {
                    const param = {
                        title:    "위드정보 확인",
                        contents: "회사근무경력을 변경하시겠습니까?",
                        accept: () => {
                            /* 변수선언 및 셋팅 영역 */
                            // 입력파라미터
                            const requestData = {
                                instCd: inquiryCnds.instCd,
                                empNo:  inquiryCnds.empNo,
                                workExpreData: userWorkExpreStatusList,
                            };
                            
                            /* 트렌젝션 호출 및 결과 셋팅 */
                            commonProxy.callAsyncSvc(requestData, {
                                txCd: "TRUSRS0017",
                                success: (_responseData: any) => {
                                    const param = {
                                        title:    "위드정보 확인",
                                        contents: "정상적으로 처리되었습니다.",
                                    };
    
                                    common.openAlert(param);

                                    // 회사 근무 경력 재조회
                                    fn_WorkExpreStatusList();
                                    // 신상정보 재조회
                                    fn_PersonalInformation();
                                },
                            });
                        }
                    }
                    common.openConfirm(param);

                break;
            }

            // 개발 경력 삭제 버튼
            case "btn_Del_Dept" : {
                if (!selectedDevelopRowKeys.length) {
                    common.openAlert({
                        title:    "위드정보 확인",
                        contents: "삭제할 행을 선택해주세요.",
                    });
                    break;
                } else {
                    const newList = userDevelopCareerStateList.map(item => {
                        if (selectedDevelopRowKeys.includes(item.id)) {
                            return { ...item, rowStsCd: "D", delYn: "D" };
                        }
                        return { ...item, rowStsCd: "", delYn: "" };
                    });
                    dispatch(Usr0302mAction.setUserDevelopCareerStateList(newList));

                    const param = {
                        title:    "위드정보 확인",
                        contents: "선택하신 개발경력을 삭제하시겠습니까?",
                        accept: () => {
                            /* 변수선언 및 셋팅 영역 */
                            // 입력파라미터
                            const requestData = {
                                instCd:            inquiryCnds.instCd,
                                empNo:             inquiryCnds.empNo,
                                developCareerData: newList,
                            };
                            
                            /* 트렌젝션 호출 및 결과 셋팅 */
                            commonProxy.callAsyncSvc(requestData, {
                                txCd: "TRUSRS0019",
                                success: (_responseData: any) => {
                                    const param = {
                                        title:    "위드정보 확인",
                                        contents: "정상적으로 처리되었습니다.",
                                    };
    
                                    common.openAlert(param);

                                    // 신상정보 재조회
                                    fn_PersonalInformation();
                                    // 개발 경력 재조회
                                    fn_DevelopCareerStateList();
                                },
                            });
                        }
                    }
                    common.openConfirm(param);
                }

                break;
            }

            // 개발 경력 경력등록 버튼(팝업오픈)
            case "btn_Add_Dept" : {
                const callback = () => {
                    // 개발 경력 재조회
                    fn_DevelopCareerStateList(inquiryCnds.empNo);
                };

                const params: any = {};
                params.title      = "경력등록";
                params.popupId    = "usr0302p2";
                params.accept     = callback;
                params.width      = 1020; 
                params.props      = {
                                     id: 0,
                                     empNo: inquiryCnds.empNo,
                                    }

                common.openPopup(params);
                break;
            }

            // 개발 경력 상세조회 버튼(팝업오픈)
            case "btn_OpenPopup_Dept_Detail" : {
                /* 변수선언 및 셋팅 영역 */
                // 입력파라미터
                const callback = () => {
                    // 개발 경력 재조회
                    fn_DevelopCareerStateList(record.empNo);
                };

                const params: any = {};
                params.title   = "경력상세";
                params.popupId = "usr0302p2";
                params.accept  = callback;
                params.width   = 1020;

                params.props = {
                    empNo: record.empNo,
                    id: e.currentTarget.dataset.id,
                };

                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     *  기능 : 회사 근무 경력 체크박스 선택
     *  설명 : 회사 근무 경력 체크박스 선택 행을 가져온다.
     */
    const [selectedWorkRowKeys, setSelectedWorkRowKeys] = React.useState<React.Key[]>([]);
    const rowSelectionWorkExpre = React.useMemo(() => ({
        selectedRowKeys: selectedWorkRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (keys: React.Key[]) => {
            setSelectedWorkRowKeys(keys);
        }
    }), [selectedWorkRowKeys]);

    /**
     *  기능 : 개발 경력 체크박스 선택
     *  설명 : 개발 경력 체크박스 선택 행을 가져온다.
     */
    const [selectedDevelopRowKeys, setSelectedDevelopRowKeys] = React.useState<React.Key[]>([]);
    const rowSelectionDevelopCareere = React.useMemo(() => ({
        selectedRowKeys: selectedDevelopRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (keys: React.Key[]) => {
            setSelectedDevelopRowKeys(keys);
        },
    }), [selectedDevelopRowKeys]);

    /**
     * 기능 : excel 다운로드
     * 설명 : 이벤트 감지 후 서버로부터 응답받은 문서를 브라우저를 이용해 로컬에 다운로드한다
     */
    const excelDownload = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            responseType: 'blob',
            txCd: 'TRUSRR0024',
            success: function (responseData: any) {
                if (!responseData) {
                    console.error('응답 없음');
                    return;
                }

                const blob = new Blob([responseData.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;

                // 날짜 생성
                const date = new Date();
                const year = date.getFullYear();
                const month = ('0' + (date.getMonth() + 1)).slice(-2);
                const day = ('0' + date.getDate()).slice(-2);

                const fileDate = `${year}${month}${day}`;

                link.setAttribute(
                    'download',
                    `개인이력카드_${fileDate}_${userPersonalInformation.userNm}.xlsx`
                );

                document.body.appendChild(link);
                link.click();

                // 정리
                link.remove();
                window.URL.revokeObjectURL(url);
            },

            error: function (err: any) {
                console.error('다운로드 실패:', err);
            },
        });
    }

    /**
     * 기능 : docx 다운로드
     * 설명 : 서버로부터 응답받은 문서를 로컬에 다운로드한다
     */
    const docxDownload = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            responseType: 'blob',
            txCd: 'TRUSRR0025',
            success: function (responseData: any) {
                if (!responseData) {
                    console.error('응답 없음');
                    return;
                }
                
                const blob = new Blob([responseData.data], {
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                });

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;

                // 날짜 생성
                const date = new Date();
                const year = date.getFullYear();
                const month = ('0' + (date.getMonth() + 1)).slice(-2);
                const day = ('0' + date.getDate()).slice(-2);

                const fileDate = `${year}${month}${day}`;

                link.setAttribute(
                    'download',
                    `개인이력카드_${fileDate}_${userPersonalInformation.userNm}.docx`
                );

                document.body.appendChild(link);
                link.click();

                // 정리
                link.remove();
                window.URL.revokeObjectURL(url);
            },

            error: function (err: any) {
                console.error('다운로드 실패:', err);
            },
        });
    }

    /**
     * 기능 : 정합성체크
     * 설명 : 정합성체크하여 저장시 오류 방지(return)
     * param : 
     * return : None
     */
    //  const fn_CheckValidation = (tran_div) => {
    //     var result = true;

    //     if (workExpreList.userId !== "admin") {
    //         if (tran_div === "save") {
    //             let result = true;

    //             /* 회사명 */
    //             if (workExpreList && workExpreList.length > 0) {
    //                 let filterd = null;
    //                 for (let i = 0; i < workExpreList.length; i++) {
    //                     filterd = workExpreList.filter(e => {
    //                         return e.corpNm === workExpreList[i].corpNm && e.workStYm.replace(/-/gi, '') === workExpreList[i].workStYm.replace(/-/gi, '')

    //                     });

    //                     if (filterd && filterd.length > 1) {
    //                         result = false;
    //                         let param = {};
    //                         param.title = "투게더솔루션 확인";
    //                         param.msg_m = '중복된 회사근무경력이 있습니다.';
    //                         comModal.openInfoAlert(param);
    //                         return result
    //                     }

    //                     if (workExpreList[i].workStYm.replace(/-/gi, '') > workExpreList[i].workEdYm.replace(/-/gi, '')) {
    //                         result = false;
    //                         let param = {};
    //                         param.title = "투게더솔루션 확인";
    //                         param.msg_m = '근무시작일자는 근무종료일자보다 작아야 합니다.';
    //                         comModal.openInfoAlert(param);
    //                         return result
    //                     }
    //                 }
    //             }

    //             return result;
    //         }
    //     }

    //     return result;
    // }
    
    return (
        <Layout>
            <Content>
                <Collapse
                    bordered         = {false}
                    defaultActiveKey = {['1']}
                    expandIcon       = {({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className        = "site-collapse-custom-collapse"
                >
                    <Panel
                        header    = "검색조건"
                        key       = "1"
                        className = "site-collapse-custom-panel"
                    >
                        <Row
                            align   = "middle"
                            justify = "space-between"
                            style   = {{ marginTop: 10 }}
                        >
                            <Space>
                                <Col>
                                    <Space>
                                        <label>직원명</label>
                                        <Select
                                            showSearch
                                            style            = {{ width: 200 }}
                                            optionFilterProp = 'label'
                                            placeholder      = "입력하세요"
                                            value            = {inquiryCnds.empNo}
                                            options          = {userOptions()}
                                            onKeyUp          = {handleOnKeyUp}
                                            // onChange         = {(val, option: any) => dispatch(Usr0302mAction.setInquiryCnds({ ...inquiryCnds, empNo: val }))}
                                            onChange={(val) => {
                                                const newCnds = {
                                                    ...inquiryCnds,
                                                    empNo: val
                                                };

                                                dispatch(Usr0302mAction.setInquiryCnds(newCnds));
                                                fn_Birth(newCnds);
                                            }}
                                            disabled         = {!common.isAdmin()}
                                        ></Select>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <label style={{ marginLeft : 10 }}>생년월일</label>
                                        <ConfigProvider
                                            locale = {locale}
                                        >
                                            <DatePicker
                                                style={{ width: 200 }}
                                                inputReadOnly
                                                allowClear = {false}
                                                picker     = 'date'
                                                value      = {inquiryCnds.birdDt ? dayjs(inquiryCnds.birdDt) : null}
                                                // onChange   = {(val, option: any) => dispatch(Usr0302mAction.setInquiryCnds({ ...inquiryCnds, birdDt: val, empNo: option.empNo }))}
                                                disabled   = {!common.isAdmin()}
                                            />
                                        </ConfigProvider>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space style={{ marginLeft: 10 }}>
                                        <Button
                                            className = "background-color-default-button"
                                            disabled  = {!common.isAdmin() && common.getEmpNo()}
                                            name      = "btn_Reset"
                                            onClick   = {onClick}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            icon      = {<SearchOutlined />}
                                            className = "background-color-primary-button"
                                            disabled  = {!common.isAdmin() && common.getEmpNo()}
                                            name      = "btn_Search"
                                            onClick   = {onClick}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Space>
                            {/* <Col>
                                <Space>
                                    <Select
                                        style={{ width: 100 }}
                                        optionFilterProp='label'
                                        options={test_eduTpCdOptions()}
                                        value={eduTpList}
                                    ></Select>
                                </Space>
                                <Space>
                                    <Select
                                        style={{ width: 100 }}
                                        optionFilterProp='label'
                                        options={test_certTpCdOptions()}
                                        value={certTpList}
                                    ></Select>
                                </Space>
                            </Col> */}
                            <Col>
                                <Space>
                                    <Button
                                        className = "background-color-default-button"
                                        name      = "btn_Docx"
                                        onClick   = {docxDownload}
                                    >
                                    워드파일 내려받기
                                    </Button>
                                    <Button
                                        className = "background-color-default-button" 
                                        name      = "btn_Excel"
                                        onClick   = {excelDownload}
                                    >
                                    엑셀파일 내려받기
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                {/* 신상정보 */}
                <Row
                    align = "middle"
                    style = {{ height: 50 }}
                >
                    <Col>
                        <Space>
                            <img className = "title-icon" alt = ""/>
                            <Text strong>신상정보</Text>
                        </Space>
                    </Col>
                    <Col
                        flex  = "auto"
                        style = {{ margin: 10, textAlign: 'right' }}
                    >
                        <Space>
                            <Button
                                className = "background-color-primary-button"
                                name      = "btn_Save_Personal"
                                onClick   = {(e: any) => onClick(e)}
                            >
                                저장
                            </Button>
                        </Space>
                    </Col>
                </Row>
                <Descriptions bordered column = {2}>
                    <Descriptions.Item label = "성명">
                        {userPersonalInformation?.userNm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "생년월일">
                        {userPersonalInformation?.birdDt || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "소속회사">
                        {userPersonalInformation?.grpNm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "입사일">
                        {userPersonalInformation?.joinDt || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "부서">
                        {userPersonalInformation?.deptNm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "직위">
                        {userPersonalInformation?.postNm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "기술등급">
                        <Space>
                            <Select
                                style            = {{ width: 130 }}
                                optionFilterProp = 'label'
                                placeholder      = "선택하세요"
                                value            = {userPersonalInformation?.tecnlLvlCd || ''}
                                options          = {tecnlLvlCdOptions()}
                                onKeyUp          = {handleOnKeyUp}
                                onChange         = {(val) => dispatch(Usr0302mAction.setUserPersonalInformation({ ...userPersonalInformation, tecnlLvlCd: val }))}
                            ></Select>
                        </Space>
                        <Button
                            icon      = {<ExclamationCircleOutlined/>}
                            style     = {{ marginLeft: 10 }}
                            size      = "middle"
                            className = "background-color-noborder-button"
                            id        = "btn_OpenPopup_Tooltip"
                            name      = "btn_OpenPopup_Tooltip"
                            onClick   = {onClick}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label = "프로젝트 경력">
                        {userPersonalInformation?.prjExpreYm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "예상등급">
                        <Space>
                            <Select
                                style            = {{ width: 130 }}
                                optionFilterProp = 'label'
                                placeholder      = "선택하세요"
                                disabled
                                value            = {userPersonalInformation?.exTecnlLvlCd || ''}
                                options          = {exTecnlLvlCdOptions()}
                            ></Select>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label = "회사근무 경력">
                        {userPersonalInformation?.workExpreYm || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label = "전문기술">
                        <Input
                            style        = {{ width: 400 }}
                            maxLength    = {300}
                            autoComplete = "off"
                            name         = "exprisCntnt"
                            id           = "inputExprisCntnt"
                            value        = {userPersonalInformation?.exprisCntnt || ''}
                            onChange     = {e => {dispatch(Usr0302mAction.setUserPersonalInformation({ ...userPersonalInformation, exprisCntnt: e.currentTarget.value }))}}
                        />
                    </Descriptions.Item>
                </Descriptions>
                {/* 학력 현황 */}
                <Collapse
                    bordered         = {false}
                    defaultActiveKey = {['1']}
                    expandIcon       = {({ isActive }) => <CaretRightOutlined rotate = {isActive ? 90 : 0} />}
                    className        = "site-collapse-custom-collapse"
                >
                    <Panel
                        header    = "학력 현황"
                        key       = "1"
                        className = "site-collapse-custom-panel" >
                        <Table
                            style      = {{ width: '100%' }}
                            size       = "small"
                            id         = "schStatusList"
                            columns    = {schStatusColumns()}
                            dataSource = {userSchStatusList}
                            rowKey     = "cd"
                            pagination = {false}
                        />
                    </Panel>
                </Collapse>
                {/* 자격증 현황 */}
                <Collapse
                    bordered         = {false}
                    defaultActiveKey = {['1']}
                    expandIcon       = {({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className        = "site-collapse-custom-collapse"
                >
                    <Panel
                        header    = "자격증 현황"
                        key       = "1"
                        className = "site-collapse-custom-panel"
                    >
                        <Table
                            style={{ width: '100%' }}
                            size       = "small"
                            id         = "certStatusList"
                            columns    = {certStatusColumns()}
                            dataSource = {userCertStatusList}
                            rowKey     = "cd"
                            pagination = {false}
                        />
                    </Panel>
                </Collapse>
                {/* 회사 근무 경력 */}
                <Collapse
                    bordered             = {false}
                    defaultActiveKey     = {['1']}
                    destroyInactivePanel = {false}
                    expandIcon           = {({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className            = "site-collapse-custom-collapse"
                >
                    <Panel
                        header    = "회사 근무 경력"
                        key       = "1"
                        className = "site-collapse-custom-panel"
                        forceRender
                    >
                        <Row align="middle" style={{ height: 50 }}>
                            <Col flex="auto">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    
                                    <span>
                                        <Text style={{color: 'blue'}}>(※ 재직중인 경우 근무종료년월일을 9999-12로 입력해주세요.)</Text>
                                    </span>

                                    <Space>
                                        <Button
                                            icon={<PlusOutlined />}
                                            name="btn_Plus_Cert"
                                            size="small"
                                            onClick={onClick}
                                        />
                                        <Button
                                            icon={<MinusOutlined />}
                                            name="btn_Minus_Cert"
                                            size="small"
                                            onClick={onClick}
                                        />
                                        <Button
                                            name="btn_Add_Work"
                                            className="background-color-primary-button"
                                            onClick={onClick}
                                        >
                                            저장
                                        </Button>
                                    </Space>

                                </div>
                            </Col>
                        </Row>
                        <Table
                            style        = {{ width: '100%' }}
                            size         = "small"
                            id           = "certStatusList"
                            columns      = {workColumns}
                            dataSource   = {userWorkExpreStatusList}
                            rowKey       = "id"
                            rowSelection = {rowSelectionWorkExpre}
                            pagination   = {false}
                        />
                    </Panel>
                </Collapse>
                {/* 개발 경력 */}
                <Collapse
                        bordered             = {false}
                        defaultActiveKey     = {['1']}
                        destroyInactivePanel = {false}
                        expandIcon           = {({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className            = "site-collapse-custom-collapse"
                    >
                        <Panel
                            header    = "개발 경력"
                            key       = "1"
                            className = "site-collapse-custom-panel"
                        >
                            <Row
                                align = "middle"
                                style = {{ marginTop: 10, textAlign:"right" }}
                            >
                                <Col
                                    flex = "auto"
                                >
                                    <Space>
                                        <Button
                                            name      = "btn_Add_Dept"
                                            className = "background-color-primary-button"
                                            onClick   = {onClick}
                                        >
                                            경력등록
                                        </Button>
                                        <Button
                                            name      = "btn_Del_Dept"
                                            className = "background-color-default-button"
                                            onClick   = {onClick}
                                        >
                                            삭제
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            <Table
                                style        = {{ marginTop: 10 }}
                                size         = "small"
                                scroll       = {{ x: 150 }}
                                id           = "developCareerList"
                                columns      = {developCareerColumns()}
                                dataSource   = {userDevelopCareerStateList}
                                rowKey       = "id"
                                rowSelection = {rowSelectionDevelopCareere}
                                pagination = {{
                                    pageSize:  pageSize,
                                    current:   current,
                                    showTotal: () => '총 ' + userDevelopCareerStateList.length + ' 건',
                                    onChange:  (page, pageSize) => {
                                        dispatch(Usr0302mAction.setCurrent(page));
                                        dispatch(Usr0302mAction.setPageSize(pageSize));
                                    },
                                }}
                            />
                        </Panel>
                    </Collapse>
            </Content>
        </Layout>
    );
};

export default Usr0302m;
